import {
  BarChart3,
  Bell,
  Bug,
  Building,
  CalendarClock,
  CalendarDays,
  CalendarRange,
  CheckSquare,
  Clapperboard,
  FileCheck2,
  FileText,
  FolderKanban,
  Handshake,
  Landmark,
  LayoutDashboard,
  LogOut,
  MessagesSquare,
  Palmtree,
  Plug,
  Receipt,
  Repeat,
  Settings,
  SquareUser,
  Sun,
  TrendingUp,
  Truck,
  User,
  Users,
  Video,
  Wallet,
} from 'lucide-react';
import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useClients } from '../../hooks/useClients';
import { useLeadStats } from '../../hooks/useLeads';
import { usePeople } from '../../hooks/usePeople';
import { useModules } from '../../hooks/useModules';
import { usePermissions } from '../../hooks/usePermissions';
import { BrandHeader } from './BrandHeader';
import { FirmioGlyph } from '../brand/FirmioAiIcon';
import { useProjects } from '../../hooks/useProjects';
import { useSubscriptions } from '../../hooks/useSubscriptions';
import { useSuppliers } from '../../hooks/useSuppliers';
import { useMeetings } from '../../hooks/useMeetingLog';
import { useBugReportStats } from '../../hooks/useBugReports';
import { openBugReport } from '../bugReports/BugReportWidget';
import { usePendingLeaveCount } from '../../hooks/useLeave';
import type { ProjectHealth } from '../../lib/projectsApi';
import { useAuthStore } from '../../store/useAuthStore';
import { useI18n } from '../../contexts/I18nContext';
import { ChatHistorySidebar } from '../chat/ChatHistorySidebar';
import { TeamChatSidebar } from '../teamChat/TeamChatSidebar';
import { VoiceSidebarPanel } from '../teamChat/VoiceSidebarPanel';
import { useTeamChatUnread } from '../../hooks/useTeamChat';
import { CompanySwitcher } from './CompanySwitcher';
import { SidebarHeaderControls } from './SidebarHeaderControls';
import { SIDEBAR_MAX_WIDTH, SIDEBAR_MIN_WIDTH, useSidebar } from './SidebarContext';
import { SidebarNavItem, type HealthDot } from './SidebarNavItem';
import { SidebarSection } from './SidebarSection';

// Map the backend health enum to a dot color.
const HEALTH_TO_DOT: Record<ProjectHealth, HealthDot> = {
  ON_TRACK: 'green',
  AT_RISK: 'yellow',
  OFF_TRACK: 'red',
};

// Statuses considered "active" enough to surface in the sidebar project list.
// Excludes LEAD, COMPLETED and CANCELLED — those clutter daily navigation and
// remain available on the full /projects list page (CLE-83).
const ACTIVE_PROJECT_STATUSES = new Set([
  'ACTIVE',
  'PLANNING',
  'ALMOST_DONE',
  'ON_HOLD',
  'IN_HOUSE',
]);

function ResizeHandle() {
  const { setWidth } = useSidebar();
  const { t } = useI18n();
  const draggingRef = useRef(false);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      draggingRef.current = true;
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';

      const onMove = (ev: PointerEvent) => {
        if (!draggingRef.current) return;
        // Sidebar is anchored to the left, so width = pointer X.
        setWidth(ev.clientX);
      };
      const onUp = () => {
        draggingRef.current = false;
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [setWidth],
  );

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={t('sidebar.resize')}
      onPointerDown={onPointerDown}
      className="absolute right-0 top-0 z-10 h-full w-1 cursor-col-resize bg-transparent transition-colors hover:bg-ring"
    />
  );
}

function SidebarContent() {
  const { isMobile, closeMobile } = useSidebar();
  const { t } = useI18n();
  const { pathname } = useLocation();
  const { can } = usePermissions();
  const { data } = useProjects({ sort: 'name', limit: 100 });
  // `active: 'true'` is required, not optional: deletes are soft (isActive =
  // false) and these list endpoints only filter when the param is present, so
  // without it the sidebar keeps listing and counting deleted rows.
  const { data: clientsData } = useClients({ limit: 1, active: 'true' });
  const { data: suppliersData } = useSuppliers({ limit: 1, active: 'true' });
  const { data: subscriptionsData } = useSubscriptions({ limit: 1, active: 'true' });
  const { data: leadStats } = useLeadStats();
  const { data: peopleData } = usePeople();
  const logout = useAuthStore((s) => s.logout);
  // Global team-chat unread badge — polled here (~15s) so it works app-wide.
  const teamChatUnread = useTeamChatUnread();
  // Concedii queue. The row itself is NOT permission-gated (everyone files
  // their own leave), but only an approver has a queue to count, so the
  // request is skipped for everybody else.
  const pendingLeave = usePendingLeaveCount(can('leave', 'canApprove'));
  // Rows with no permission flag of their own follow the product modules
  // (Firmio Console ceiling + Settings → Module).
  const { isOn } = useModules();
  const leaveEnabled = isOn('leave');

  // Permission gates for each navigable section (deny by default).
  const canDashboard = can('dashboard', 'canView');
  const canDailySummary = can('dailySummary', 'canView');
  const canWeeklySummary = can('weeklySummary', 'canView');
  const canAi = can('aiAssistant', 'canAccess');
  const canTasks = can('tasks', 'canView');
  const canTaskReports = can('tasks', 'canViewReports');
  const canLeads = can('leads', 'canView');
  const canMeetings = can('meetings', 'canView');
  const canMeet = can('videoMeetings', 'canView');
  // Meetings badge: the total, like projects/clients. One row is enough to
  // read `meta.total`; the list itself lives on /meetings.
  const { data: meetingsData } = useMeetings({ limit: 1 }, { enabled: canMeetings });
  const canProjects = can('projects', 'canView');
  const canClients = can('clients', 'canView');
  const canSuppliers = can('suppliers', 'canView');
  const canSubscriptions = can('subscriptions', 'canView');
  const canFinancials = can('financials', 'canView');
  const canPeople = can('people', 'canView');
  // Actori: opt-in module (Genius Media); the mask also turns canView off.
  const canActors = isOn('actors') && can('actors', 'canView');
  const canSettings = can('settings', 'canAccessSettings');

  // Untriaged bug reports, shown as a count on the "Bug-uri raportate" entry
  // below Settings. ADMIN-only on both ends: `/api/bug-reports/stats` is
  // `authorizeRole('ADMIN')`, so asking as anyone else would only collect 403s.
  const isAdmin = useAuthStore((s) => s.user?.role) === 'ADMIN';
  const { data: bugStats } = useBugReportStats(isAdmin);

  // Close the mobile overlay after any navigation.
  const handleNavigate = isMobile ? closeMobile : undefined;

  // On the chat routes the whole sidebar swaps to that chat's conversation
  // list (Discord-style). Swapping here keeps both the mobile slide-in and the
  // desktop <aside> shells working unchanged.
  if (pathname.startsWith('/team-chat') && isOn('teamChat')) {
    return <TeamChatSidebar onNavigate={handleNavigate} />;
  }
  if (pathname.startsWith('/chat')) {
    return <ChatHistorySidebar onNavigate={handleNavigate} />;
  }

  // Sub-items list only the active projects, but the badge counts ALL projects
  // (finalized included) — `meta.total` is the unfiltered server-side total.
  const projects = (data?.projects ?? []).filter((p) => ACTIVE_PROJECT_STATUSES.has(p.status));
  const totalProjectCount = data?.meta.total ?? projects.length;
  // Badges only: one row per query, the true total comes from `meta.total`
  // (active rows only, matching the `active: 'true'` queries above).
  const totalClientCount = clientsData?.meta.total ?? 0;
  const totalSupplierCount = suppliersData?.meta.total ?? 0;
  const totalSubscriptionCount = subscriptionsData?.meta.total ?? 0;
  // Active team members, alphabetical by full name. Derived from the already
  // loaded people list — no extra API call (CLE-213).
  const activePeople = (peopleData ?? [])
    .filter((p) => p.isActive)
    .sort((a, b) => a.fullName.localeCompare(b.fullName));
  // Badge = the leads the list shows by default: every non-archived lead,
  // won ones included (counting only ACTIVE stages left the badge below the
  // list's own count, which read as a bug).
  const activeLeadCount = leadStats?.totalLeads ?? 0;

  return (
    <nav className="flex h-full flex-col overflow-y-auto pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)]">
      {/* Brand / logo */}
      <div className="flex items-center justify-between gap-2 px-4 py-4 pt-[calc(1rem+env(safe-area-inset-top))]">
        {/* Only the mobile drawer needs to wrap: there the close button eats
            another 44px of the row. The desktop sidebar has no close button and
            is resizable, so it keeps the single truncated line. */}
        <BrandHeader wrap={isMobile} nameClassName="text-sidebar-foreground" />
        <SidebarHeaderControls />
      </div>

      {/* Global firm switcher (multi-company) — hidden with ≤1 firm. */}
      <CompanySwitcher />

      <div className="flex-1 space-y-1 px-3 pb-6">
        {/* Overview group */}
        {canDashboard && (
          <SidebarNavItem
            to="/dashboard"
            label={t('sidebar.dashboard')}
            icon={LayoutDashboard}
            onNavigate={handleNavigate}
          />
        )}
        {canDailySummary && (
          <SidebarNavItem
            to="/daily-summary"
            label={t('sidebar.dailySummary')}
            icon={Sun}
            onNavigate={handleNavigate}
          />
        )}
        {canWeeklySummary && (
          <SidebarNavItem
            to="/weekly-summary"
            label={t('sidebar.weeklySummary')}
            icon={CalendarRange}
            onNavigate={handleNavigate}
          />
        )}
        {canAi && (
          <SidebarNavItem
            to="/chat"
            label={t('sidebar.chat')}
            icon={FirmioGlyph}
            onNavigate={handleNavigate}
          />
        )}
        {/* Firmio MCP — the same assistant from Claude Desktop / claude.ai. */}
        {canAi && isOn('mcp') && (
          <SidebarNavItem
            to="/mcp"
            label={t('sidebar.mcp')}
            icon={Plug}
            onNavigate={handleNavigate}
          />
        )}
        {/* Firmio Meet — video meetings with guests by link, right under
            Firmio AI (Firmio Voice has no entry: it is the voice mode of the
            assistant, reached from the chat header). */}
        {canMeet && (
          <SidebarNavItem
            to="/meet"
            label={t('sidebar.meet')}
            icon={Video}
            onNavigate={handleNavigate}
          />
        )}
        {/* Team chat — auth-only (like Reminders), never permission-gated;
            only the product module can hide it. Badge = total unread messages
            across all conversations, in the same red circle the conversation
            rows use, so "something new in chat" is visible from any page. */}
        {isOn('teamChat') && (
          <SidebarNavItem
            to="/team-chat"
            label={t('sidebar.teamChat')}
            icon={MessagesSquare}
            badge={teamChatUnread.data?.total}
            badgeTone="unread"
            onNavigate={handleNavigate}
          />
        )}
        <Divider />
        {/* Personal reminders — auth-only feature, never permission-gated. */}
        {isOn('reminders') && (
          <SidebarNavItem
            to="/reminders"
            label={t('sidebar.reminders')}
            icon={Bell}
            onNavigate={handleNavigate}
          />
        )}
        {/* Concedii — personal like Remindere: filing your own leave needs no
            permission, so the row is always there. The badge is the approval
            queue, which is why it only appears for an approver. */}
        {leaveEnabled && (
          <SidebarNavItem
            to="/concedii"
            label={t('sidebar.leave')}
            icon={Palmtree}
            badge={pendingLeave.data}
            onNavigate={handleNavigate}
          />
        )}
        {/* Private per-user task list — visible only to its owner. Lives next
            to Reminders (both are personal, not team-wide). */}
        {canTasks && (
          <SidebarNavItem
            to="/personal-tasks"
            label={t('sidebar.personalTasks')}
            icon={SquareUser}
            onNavigate={handleNavigate}
          />
        )}
        {canTasks && (
          <>
            <Divider />
            {/* Tasks (built-in ticketing — CLE-155) */}
            <SidebarNavItem
              to="/tasks"
              label={t('sidebar.tasks')}
              icon={CheckSquare}
              onNavigate={handleNavigate}
            />
            {/* Operational calendar (monthly task grid + project/team Gantt). */}
            {isOn('calendar') && (
              <SidebarNavItem
                to="/calendar"
                label={t('sidebar.calendar')}
                icon={CalendarDays}
                onNavigate={handleNavigate}
              />
            )}
            {canTaskReports && (
              <SidebarNavItem
                to="/reports/tasks"
                label={t('sidebar.taskReports')}
                icon={BarChart3}
                onNavigate={handleNavigate}
              />
            )}
          </>
        )}

        {canMeetings && (
          <>
            <Divider />
            {/* Meetings with external people (Întâlniri) — RBAC module `meetings`. */}
            <SidebarNavItem
              to="/meetings"
              label={t('sidebar.meetings')}
              icon={Handshake}
              badge={meetingsData?.meta.total}
              onNavigate={handleNavigate}
            />
          </>
        )}

        {canLeads && (
          <>
            <Divider />
            {/* Leads: a plain link to the list (the pipelines are picked on the page). */}
            <SidebarNavItem
              to="/leads"
              label={t('sidebar.leads')}
              icon={TrendingUp}
              badge={activeLeadCount}
              onNavigate={handleNavigate}
            />
          </>
        )}

        {canProjects && (
          <>
            <Divider />
            {/* Projects (collapsible). Badge = TOTAL project count (finalized
                included); the sub-items below list only the active ones. */}
            <SidebarSection
              id="projects"
              label={t('sidebar.projects')}
              icon={FolderKanban}
              badge={totalProjectCount}
            >
              <SidebarNavItem
                to="/projects"
                label={t('sidebar.allProjects')}
                nested
                end
                onNavigate={handleNavigate}
              />
              {projects.map((project) => (
                <SidebarNavItem
                  key={project.id}
                  to={`/projects/${project.id}`}
                  label={project.name}
                  nested
                  dot={HEALTH_TO_DOT[project.health]}
                  onNavigate={handleNavigate}
                />
              ))}
            </SidebarSection>
          </>
        )}

        {canClients && (
          <>
            <Divider />
            {/* Clients: a plain link to the list. Only Projects expands in the sidebar. */}
            <SidebarNavItem
              to="/clients"
              label={t('sidebar.clients')}
              icon={Building}
              badge={totalClientCount}
              onNavigate={handleNavigate}
            />
          </>
        )}

        {canSuppliers && (
          <>
            <Divider />
            <SidebarNavItem
              to="/suppliers"
              label={t('sidebar.suppliers')}
              icon={Truck}
              badge={totalSupplierCount}
              onNavigate={handleNavigate}
            />
          </>
        )}

        {canSubscriptions && (
          <>
            <Divider />
            <SidebarNavItem
              to="/subscriptions"
              label={t('sidebar.subscriptions')}
              icon={Repeat}
              badge={totalSubscriptionCount}
              onNavigate={handleNavigate}
            />
          </>
        )}

        {canFinancials && (
          <>
            <Divider />
            {/* Financials (collapsible) */}
            <SidebarSection id="financials" label={t('sidebar.financials')} icon={Wallet}>
              <SidebarNavItem
                to="/financials"
                label={t('sidebar.financialsOverview')}
                nested
                end
                onNavigate={handleNavigate}
              />
              {isOn('invoices') && (
                <SidebarNavItem
                  to="/invoices"
                  label={t('sidebar.invoices')}
                  icon={Receipt}
                  nested
                  onNavigate={handleNavigate}
                />
              )}
              {isOn('spv') && (
                <SidebarNavItem
                  to="/financials/spv"
                  label={t('sidebar.spv')}
                  icon={FileCheck2}
                  nested
                  onNavigate={handleNavigate}
                />
              )}
              {isOn('bankStatements') && (
                <SidebarNavItem
                  to="/financials/bank-statements"
                  label={t('sidebar.bankStatements')}
                  icon={Landmark}
                  nested
                  onNavigate={handleNavigate}
                />
              )}
              {isOn('cashflow') && (
                <SidebarNavItem
                  to="/financials/cashflow"
                  label={t('sidebar.cashflow')}
                  icon={CalendarClock}
                  nested
                  onNavigate={handleNavigate}
                />
              )}
              {isOn('monthlyReports') && (
                <SidebarNavItem
                  to="/financials/reports"
                  label={t('sidebar.monthlyReports')}
                  icon={FileText}
                  nested
                  onNavigate={handleNavigate}
                />
              )}
            </SidebarSection>
          </>
        )}

        {canPeople && (
          <>
            <Divider />
            {/* Team: a plain link; members are added from the /people page. */}
            <SidebarNavItem
              to="/people"
              label={t('sidebar.people')}
              icon={Users}
              badge={activePeople.length}
              onNavigate={handleNavigate}
            />
          </>
        )}
        {canActors && (
          <SidebarNavItem
            to="/actors"
            label={t('sidebar.actors')}
            icon={Clapperboard}
            onNavigate={handleNavigate}
          />
        )}

        {/* Email history (CLE-310) now lives in Settings → Istoric emailuri;
            it is an admin audit view, not day-to-day navigation. */}

        {canSettings && (
          <>
            <Divider />
            <SidebarNavItem
              to="/settings"
              label={t('sidebar.settings')}
              icon={Settings}
              onNavigate={handleNavigate}
            />
          </>
        )}

        {/* Its own entry rather than a tab inside Settings: that strip is
            twenty tabs long and scrolls sideways, so the list sat off the
            right edge and the one person who needs it could not find it.
            Admin-only, like the endpoints behind it. */}
        {isAdmin && (
          <SidebarNavItem
            to="/bug-reports"
            label={t('sidebar.bugReports')}
            icon={Bug}
            nested
            badge={bugStats?.newCount}
            onNavigate={handleNavigate}
          />
        )}
      </div>

      {/* Profile link (CLE-434) + Log out — pinned at the very bottom of the
          sidebar. The language switch now lives on the profile page and the
          Test Mode toggle is hidden from the logged-in menu. */}
      <div className="mt-auto space-y-2 border-t border-sidebar-border px-3 py-3">
        {/* Opens the bug-report panel over the current page (no floating button). */}
        <button
          type="button"
          onClick={() => {
            handleNavigate?.();
            openBugReport();
          }}
          className="flex min-h-10 w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-bold text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
        >
          <Bug size={18} />
          {t('bugReport.widget.open')}
        </button>
        <SidebarNavItem
          to="/profile"
          label={t('sidebar.profile')}
          icon={ProfileNavIcon}
          onNavigate={handleNavigate}
        />
        <button
          type="button"
          onClick={() => void logout()}
          className="flex min-h-10 w-full items-center gap-2 rounded-md border border-sidebar-border px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-foreground"
        >
          <LogOut size={16} />
          {t('sidebar.logout')}
        </button>
      </div>
    </nav>
  );
}

/**
 * Group separator. `my-1` and not `my-2`: the list's own `space-y-1` already
 * wins over the divider's margin-top (higher specificity), so `my-2` only ever
 * applied below the line — 4px above, 8px below. That asymmetry is what made
 * the undivided top links look cramped next to the divided section groups.
 */
function Divider() {
  return <div className="my-1 border-t border-sidebar-border" />;
}

export function Sidebar() {
  const { width, isMobile, mobileOpen, closeMobile } = useSidebar();

  // Lock body scroll while the mobile overlay is open.
  useEffect(() => {
    if (isMobile && mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [isMobile, mobileOpen]);

  if (isMobile) {
    return (
      <>
        {/* Backdrop */}
        <div
          aria-hidden
          onClick={closeMobile}
          className={`fixed inset-0 z-40 bg-black/60 transition-opacity duration-200 ${
            mobileOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
          }`}
        />
        {/* Slide-in panel */}
        <aside
          className={`sidebar-surface fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground shadow-xl transition-transform duration-200 ease-in-out ${
            mobileOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="min-h-0 flex-1">
            <SidebarContent />
          </div>
          <VoiceSidebarPanel />
        </aside>
      </>
    );
  }

  return (
    <aside
      className="sidebar-surface relative hidden shrink-0 border-r border-sidebar-border bg-sidebar text-sidebar-foreground md:block"
      style={{
        width,
        minWidth: SIDEBAR_MIN_WIDTH,
        maxWidth: SIDEBAR_MAX_WIDTH,
      }}
    >
      {/* The layout shell is viewport-locked (only <main> scrolls), so the
          sidebar just fills its column; the inner nav owns its own scroll.
          The voice panel sits OUTSIDE the swappable content, bottom-left like
          Discord's, so it stays put while the nav swaps between the app, the
          AI chat history and the team chat list. */}
      <div className="flex h-full flex-col">
        <div className="min-h-0 flex-1">
          <SidebarContent />
        </div>
        <VoiceSidebarPanel />
      </div>
      <ResizeHandle />
    </aside>
  );
}

/** The profile entry shows your photo once you set one (Profil → Poza de profil). */
function ProfileNavIcon({ size = 16, className }: { size?: number; className?: string }) {
  const avatarUrl = useAuthStore((s) => s.user?.avatarUrl);
  if (!avatarUrl) return <User size={size} className={className} />;
  return (
    <img
      src={avatarUrl}
      alt=""
      className={`shrink-0 rounded-full object-cover ${className ?? ''}`}
      style={{ width: size, height: size }}
    />
  );
}
